import type { LcDirectory, LcNapSource, LcScanInfo, LcStats, LcStatusKey, LcTierDef, LcToneKey } from '@elva/ui';
import { growthGet, growthList, growthPost, growthPostAsync } from '@/data/http';
import { deepCamel, enumLower, relativeTimeFromIso, str } from '@/data/adapt';

const CITATIONS_PAGE_SIZE = 100;

interface BeTierMeta {
  tier?: string;
  title?: string;
  subtitle?: string;
  badge?: string;
}

interface BeCitationItem {
  id?: number | string;
  directory_key?: string;
  name?: string;
  logo?: string;
  status?: string;
  citation_url?: string;
  inconsistency_type?: string | null;
  last_checked?: string;
  issue?: string | null;
  tier?: BeTierMeta;
}

interface BeCitationsListPayload {
  items?: BeCitationItem[];
  tiers?: Array<{
    tier?: number;
    title?: string;
    subtitle?: string;
    badge?: string;
    directories?: Array<Record<string, unknown>>;
  }>;
  stats?: Record<string, number>;
}

interface BeCitationSummary {
  gauge?: { consistent?: number; total?: number; pct?: number };
  tiles?: Record<string, number>;
  recent_fixes?: number;
  last_scan?: string;
  next_scan?: string;
}

interface BeSourceOfTruth {
  practice?: {
    practice_name?: string;
    phone_number?: string;
    address?: string | Record<string, unknown>;
    website?: string;
  };
}

export interface CitationsData {
  tiers: LcTierDef[];
  stats: LcStats;
  napSource?: LcNapSource;
  scanInfo?: LcScanInfo;
}

const DIRECTORY_META: Record<string, { name: string; logo: string }> = {
  google_maps: { name: 'Google Business Profile', logo: 'G' },
  google: { name: 'Google Business Profile', logo: 'G' },
  gbp: { name: 'Google Business Profile', logo: 'G' },
  yelp: { name: 'Yelp', logo: 'Y' },
  facebook: { name: 'Facebook', logo: 'f' },
  apple_maps: { name: 'Apple Maps', logo: 'A' },
  bing: { name: 'Bing Places', logo: 'B' },
  instagram: { name: 'Instagram', logo: 'IG' },
  nextdoor: { name: 'Nextdoor', logo: 'N' },
  duckduckgo: { name: 'DuckDuckGo Maps', logo: 'D' },
  healthgrades: { name: 'Healthgrades', logo: 'HG' },
  zocdoc: { name: 'Zocdoc', logo: 'Z' },
  vitals: { name: 'Vitals.com', logo: 'V' },
  webmd: { name: 'WebMD Directory', logo: 'W' },
  ratemds: { name: 'RateMDs', logo: 'R' },
  yellowpages: { name: 'YellowPages', logo: 'YP' },
  foursquare: { name: 'Foursquare', logo: '4S' },
  mapquest: { name: 'MapQuest', logo: 'MQ' },
};

const TIER_TONE: Record<1 | 2 | 3, LcToneKey> = {
  1: 'ai',
  2: 'info',
  3: 'neutral',
};

const DEFAULT_TIER_COPY: Record<1 | 2 | 3, { title: string; subtitle: string; badge?: string }> = {
  1: {
    title: 'Tier 1 — Critical directories',
    subtitle: 'The highest-impact citations. Matters most for Map Pack ranking.',
    badge: 'High impact',
  },
  2: {
    title: 'Tier 2 — Dental / medical directories',
    subtitle: 'Industry-specific listings. Important for patient discovery.',
  },
  3: {
    title: 'Tier 3 — General business directories',
    subtitle: 'Broad citations that reinforce your online footprint.',
  },
};

function numSafe(value: unknown, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value) : (value as number);
  return Number.isFinite(n) ? (n as number) : fallback;
}

function formatDirectoryKey(key: string): string {
  return key
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatLogo(logo: string, name: string): string {
  if (!logo || logo === '—') return name.slice(0, 2).toUpperCase();
  if (logo.length <= 3) return logo.toUpperCase();
  return logo.slice(0, 2).toUpperCase();
}

function directoryMeta(key: string) {
  return (
    DIRECTORY_META[key] ?? {
      name: formatDirectoryKey(key),
      logo: key.slice(0, 2).toUpperCase(),
    }
  );
}

function parseTierIndex(tierKey: string | undefined, fallback: 1 | 2 | 3 = 3): 1 | 2 | 3 {
  if (!tierKey) return fallback;
  const match = tierKey.match(/tier_(\d+)/);
  const n = match ? Number(match[1]) : fallback;
  if (n <= 1) return 1;
  if (n === 2) return 2;
  return 3;
}

function formatWhen(isoOrLabel: string): string {
  if (!isoOrLabel || isoOrLabel === '—') return '—';
  if (isoOrLabel.includes('T') || isoOrLabel.endsWith('Z')) {
    return relativeTimeFromIso(isoOrLabel);
  }
  return isoOrLabel;
}

function adaptStatus(value: unknown): LcStatusKey {
  const status = enumLower(str(value, 'missing'), 'missing');
  if (['consistent', 'ok', 'valid', 'synced'].includes(status)) return 'consistent';
  if (['inconsistent', 'mismatch', 'correcting'].includes(status)) return 'inconsistent';
  if (['duplicate', 'duplicates'].includes(status)) return 'duplicate';
  if (['unclaimed', 'claimable'].includes(status)) return 'unclaimed';
  return 'missing';
}

function formatPracticeAddress(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const row = deepCamel<Record<string, unknown>>(value);
    const parts = [
      row.line1 ?? row.street ?? row.addressLine1,
      row.line2 ?? row.addressLine2,
      row.city,
      row.state ?? row.region,
      row.postalCode ?? row.zip,
    ]
      .map((part) => str(part, '').trim())
      .filter(Boolean);
    if (parts.length) return parts.join(', ');
  }
  return str(value);
}

function adaptNapSource(be?: BeSourceOfTruth): LcNapSource | undefined {
  const practice = be?.practice;
  if (!practice) return undefined;
  const c = deepCamel<Record<string, unknown>>(practice);
  const name = str(c.practiceName, '');
  const phone = str(c.phoneNumber, '');
  const address = formatPracticeAddress(c.address);
  const website = str(c.website, '');
  if (!name && !phone && address === '—' && !website) return undefined;
  return {
    name: name || '—',
    address,
    phone: phone || '—',
    website: website || '—',
  };
}

function adaptDirectory(row: Record<string, unknown>): LcDirectory {
  const c = deepCamel<Record<string, unknown>>(row);
  const directoryKey = str(c.directoryKey ?? c.id, 'directory');
  const meta = directoryMeta(directoryKey);
  const name = str(c.name, meta.name);
  const logo = formatLogo(str(c.logo, ''), name);
  const base: LcDirectory = {
    id: String(c.id ?? directoryKey),
    directoryKey,
    name,
    logo,
    status: adaptStatus(c.status),
    lastChecked: formatWhen(str(c.lastChecked, '')),
  };
  const listingUrl = str(c.citationUrl, '');
  if (listingUrl && listingUrl !== '—') base.listingUrl = listingUrl;
  const issue = str(c.issue, '');
  if (issue && issue !== '—') base.issue = issue;
  return base;
}

type AdaptedCitationRow = LcDirectory & { tierIndex: 1 | 2 | 3; tierMeta: BeTierMeta };

function adaptCitationItem(row: Record<string, unknown>): AdaptedCitationRow {
  const c = deepCamel<Record<string, unknown>>(row);
  const directoryKey = str(c.directoryKey, str(c.id, 'directory'));
  const meta = directoryMeta(directoryKey);
  const tierMeta = (c.tier as BeTierMeta | undefined) ?? {};
  const tierIndex = parseTierIndex(tierMeta.tier);
  const name = str(c.name, meta.name);
  const logo = formatLogo(str(c.logo, ''), name);
  const status = adaptStatus(c.status);

  const base: AdaptedCitationRow = {
    id: String(c.id ?? directoryKey),
    directoryKey,
    name,
    logo,
    status,
    lastChecked: formatWhen(str(c.lastChecked, '')),
    tierIndex,
    tierMeta,
  };

  const listingUrl = str(c.citationUrl, '');
  if (listingUrl && listingUrl !== '—') base.listingUrl = listingUrl;

  const wireIssue = str(c.issue, '');
  const inconsistency = str(c.inconsistencyType, '').toLowerCase().replace(/-/g, '_');
  if (wireIssue && wireIssue !== '—') {
    base.issue = wireIssue;
  } else if (inconsistency && inconsistency !== 'none') {
    base.issue = inconsistency.replace(/_/g, ' ').replace(/\b\w/g, (ch: string) => ch.toUpperCase());
  }

  return base;
}

function computeStats(directories: LcDirectory[]): LcStats {
  const count = (status: LcStatusKey) => directories.filter((d) => d.status === status).length;
  return {
    total: directories.length,
    consistent: count('consistent'),
    inconsistent: count('inconsistent'),
    missing: count('missing'),
    duplicate: count('duplicate'),
    unclaimed: count('unclaimed'),
    recentFixes: 0,
  };
}

function buildTiersFromItems(items: AdaptedCitationRow[]): LcTierDef[] {
  const groups = new Map<1 | 2 | 3, { meta: BeTierMeta; directories: LcDirectory[] }>();

  for (const item of items) {
    const { tierIndex, tierMeta, ...directory } = item;
    const group = groups.get(tierIndex) ?? { meta: tierMeta, directories: [] };
    if (!group.meta.title && tierMeta.title) group.meta = tierMeta;
    group.directories.push(directory);
    groups.set(tierIndex, group);
  }

  return ([1, 2, 3] as const)
    .filter((tierIndex) => groups.has(tierIndex))
    .map((tierIndex, index) => {
      const group = groups.get(tierIndex)!;
      const defaults = DEFAULT_TIER_COPY[tierIndex];
      return {
        tier: tierIndex,
        title: str(group.meta.title, defaults.title),
        subtitle: str(group.meta.subtitle, defaults.subtitle),
        ...(group.meta.badge || defaults.badge ? { badge: str(group.meta.badge, defaults.badge) } : {}),
        tone: TIER_TONE[tierIndex],
        directories: group.directories,
        defaultOpen: index === 0,
      };
    });
}

function adaptTieredResponse(be: BeCitationsListPayload): CitationsData {
  const tiers: LcTierDef[] = (be.tiers ?? []).map((tier, index) => {
    const toneKeys: LcToneKey[] = ['ai', 'info', 'neutral'];
    const tierIndex = numSafe(tier.tier, index + 1) as 1 | 2 | 3;
    return {
      tier: tierIndex,
      title: str(tier.title, DEFAULT_TIER_COPY[tierIndex]?.title ?? `Tier ${index + 1}`),
      subtitle: str(tier.subtitle, DEFAULT_TIER_COPY[tierIndex]?.subtitle ?? ''),
      ...(tier.badge ? { badge: tier.badge } : {}),
      tone: toneKeys[index] ?? 'neutral',
      directories: (tier.directories ?? []).map((row) => adaptDirectory(row)),
      defaultOpen: index === 0,
    };
  });

  const allDirs = tiers.flatMap((tier) => tier.directories);
  const s = be.stats ?? {};
  const stats: LcStats = {
    total: numSafe(s.total, allDirs.length),
    consistent: numSafe(s.consistent, computeStats(allDirs).consistent),
    inconsistent: numSafe(s.inconsistent, computeStats(allDirs).inconsistent),
    missing: numSafe(s.missing, computeStats(allDirs).missing),
    duplicate: numSafe(s.duplicate, computeStats(allDirs).duplicate),
    unclaimed: numSafe(s.unclaimed, computeStats(allDirs).unclaimed),
    recentFixes: numSafe(s.recent_fixes ?? s.recentFixes),
  };

  return { tiers, stats };
}

function adaptFlatItems(items: BeCitationItem[]): CitationsData {
  const adapted = items.map((row) => adaptCitationItem(row as Record<string, unknown>));
  const directories = adapted.map(({ tierIndex: _tierIndex, tierMeta: _tierMeta, ...dir }) => dir);
  return {
    tiers: buildTiersFromItems(adapted),
    stats: computeStats(directories),
  };
}

export function adaptCitations(be: BeCitationsListPayload): CitationsData {
  if (be.items?.length) return adaptFlatItems(be.items);
  if (be.tiers?.length) return adaptTieredResponse(be);
  return {
    tiers: [],
    stats: {
      total: 0,
      consistent: 0,
      inconsistent: 0,
      missing: 0,
      duplicate: 0,
      unclaimed: 0,
      recentFixes: 0,
    },
  };
}

function applySummary(data: CitationsData, summary?: BeCitationSummary): CitationsData {
  if (!summary) return data;

  const tiles = summary.tiles ?? {};
  const stats: LcStats = {
    total: numSafe(summary.gauge?.total, data.stats.total),
    consistent: numSafe(tiles.consistent ?? summary.gauge?.consistent, data.stats.consistent),
    inconsistent: numSafe(tiles.inconsistent, data.stats.inconsistent),
    missing: numSafe(tiles.missing, data.stats.missing),
    duplicate: numSafe(tiles.duplicate, data.stats.duplicate),
    unclaimed: numSafe(tiles.unclaimed, data.stats.unclaimed),
    recentFixes: numSafe(summary.recent_fixes, data.stats.recentFixes),
  };

  const scanInfo: LcScanInfo = {};
  const lastScan = str(summary.last_scan, '');
  const nextScan = str(summary.next_scan, '');
  if (lastScan) scanInfo.lastScan = formatWhen(lastScan);
  if (nextScan) scanInfo.nextScan = formatWhen(nextScan);

  return {
    ...data,
    stats,
    ...(Object.keys(scanInfo).length ? { scanInfo } : {}),
  };
}

export async function fetchCitations(page = 1, limit = CITATIONS_PAGE_SIZE): Promise<CitationsData> {
  const [list, summary, sourceOfTruth] = await Promise.all([
    growthList<BeCitationItem>('/seo/citations/', { page, limit }).catch(() => ({ items: [] })),
    growthGet<BeCitationSummary>('/seo/citations/summary/').catch(() => undefined),
    growthGet<BeSourceOfTruth>('/seo/citations/source-of-truth/').catch(() => undefined),
  ]);

  const data = adaptCitations({ items: list.items });
  const napSource = adaptNapSource(sourceOfTruth);
  const withSummary = applySummary(data, summary);

  return {
    ...withSummary,
    ...(napSource ? { napSource } : {}),
  };
}

function citationActionPath(directoryKey: string, action: string): string {
  return `/seo/citations/${encodeURIComponent(directoryKey)}/${action}/`;
}

export async function suggestMoreCitations() {
  return growthPostAsync('/seo/citations/suggest-more/');
}

export async function createCitationListing(directoryKey: string) {
  return growthPost(citationActionPath(directoryKey, 'create-listing'));
}

export async function claimCitationListing(directoryKey: string) {
  return growthPost(citationActionPath(directoryKey, 'claim-listing'));
}

export async function helpResolveCitation(directoryKey: string) {
  return growthPost(citationActionPath(directoryKey, 'help-resolve'));
}

export async function pushCitationCorrection(directoryKey: string) {
  return growthPost(citationActionPath(directoryKey, 'correction-push'));
}

export { CITATIONS_PAGE_SIZE };
